// Centralized waitlist API config + request helpers (WAIT-4B).
// Same-origin RELATIVE paths only — no hardcoded hostnames. The ingress routes
// /api/* to the backend in both preview and production.
export const WAITLIST_OPTIONS_PATH = "/api/waitlist/options";
export const WAITLIST_SUBMIT_PATH = "/api/waitlist";
const TIMEOUT_MS = 12000;

async function requestWithTimeout(path, init) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    return await fetch(path, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

// Loads + validates the options contract. Returns { sports, personas } using
// { code, label } shape. Throws { kind } on any failure (no payload/body in error).
export async function fetchWaitlistOptions() {
  let res;
  try {
    res = await requestWithTimeout(WAITLIST_OPTIONS_PATH, {
      headers: { Accept: "application/json" },
    });
  } catch (_e) {
    throw { kind: "network" };
  }
  if (!res.ok) throw { kind: "http", status: res.status };

  let data;
  try {
    data = await res.json();
  } catch (_e) {
    throw { kind: "malformed" };
  }

  if (!data || !Array.isArray(data.sports) || !Array.isArray(data.personas)) {
    throw { kind: "malformed" };
  }
  const mapItem = (x) =>
    x && typeof x.code === "string" && typeof x.name === "string"
      ? { code: x.code, label: x.name }
      : null;
  const sports = data.sports.map(mapItem).filter(Boolean);
  const personas = data.personas.map(mapItem).filter(Boolean);
  if (sports.length === 0 || personas.length === 0) throw { kind: "malformed" };
  return { sports, personas };
}

// Submits a signup. Returns a classified result. Success = any 2xx (the API
// intentionally returns the same generic response for new and duplicate emails).
// Never logs or echoes the payload/response body.
export async function submitWaitlistSignup(payload) {
  let res;
  try {
    res = await requestWithTimeout(WAITLIST_SUBMIT_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (_e) {
    return { ok: false, kind: "network" };
  }
  if (res.ok) {
    // A 2xx alone is NOT success: require a valid JSON object with status "ok"
    // and a non-empty string message. Empty/204/HTML/text/malformed/array/null
    // bodies fall through to a recoverable error. Body is never logged.
    let body;
    try {
      body = await res.json();
    } catch (_e) {
      return { ok: false, kind: "server" };
    }
    if (
      body &&
      typeof body === "object" &&
      !Array.isArray(body) &&
      body.status === "ok" &&
      typeof body.message === "string" &&
      body.message.trim() !== ""
    ) {
      return { ok: true };
    }
    return { ok: false, kind: "server" };
  }
  const status = res.status;
  if (status === 429) return { ok: false, kind: "rate_limited" };
  if (status === 422) return { ok: false, kind: "validation" };
  return { ok: false, kind: "server" };
}
